import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { fontMono } from "src/design/typography";
import { Loader, SideDrawer } from "src/grading/components";
import { usePermissions } from "src/grading/hooks/usePermissions";
import { useGetAuditQuery } from "src/store/reducers/sessions/sessionsApiSlice";

interface AuditDrawerProps {
  sessionId: string;
  nameOf: (studentId: string | null) => string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const text = (value: unknown) =>
  value === null || value === undefined ? "" : String(value);

/** Who changed what, when, from what to what (FR-026). Visible to roles with grading audit permission. */
const AuditDrawer = ({
  sessionId,
  nameOf,
  open,
  onOpen,
  onClose,
}: AuditDrawerProps) => {
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
  const canRead = hasPermissions("read", "grading-service:audit");
  const { data, isLoading } = useGetAuditQuery(sessionId, {
    skip: !open || !canRead,
  });
  if (!canRead) return null;
  const title = t("review.auditLog", { defaultValue: "Audit log" });
  return (
    <>
      <Button variant="outlined" color="secondary" onClick={onOpen}>
        {title}
      </Button>
      <SideDrawer open={open} onClose={onClose} title={title}>
        {isLoading ? (
          <Loader />
        ) : data?.length === 0 ? (
          <Typography color="text.secondary">
            {t("review.noAudit", { defaultValue: "No changes recorded." })}
          </Typography>
        ) : (
          <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {(data ?? []).map((row, i) => {
              const studentId = row.studentId ? text(row.studentId) : null;
              const actorId = text(row.actorId);
              return (
                <Box
                  component="li"
                  key={text(row.id) || i}
                  sx={{
                    py: 1.5,
                    borderTop: 1,
                    borderColor: "divider",
                    "&:first-of-type": { borderTop: 0, pt: 0 },
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                    {`${text(row.action)} · ${text(row.actorType)}${actorId ? ` ${actorId.slice(0, 8)}` : ""}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    component="p"
                    color="text.secondary"
                    sx={{ fontFamily: fontMono }}
                  >
                    {row.at ? new Date(text(row.at)).toLocaleString() : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {studentId ? `${nameOf(studentId)} · ` : ""}
                    {row.oldValue
                      ? `${t("review.auditFrom", { defaultValue: "from" })} ${JSON.stringify(row.oldValue)} `
                      : ""}
                    {row.newValue
                      ? `${t("review.auditTo", { defaultValue: "to" })} ${JSON.stringify(row.newValue)}`
                      : ""}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </SideDrawer>
    </>
  );
};

export default AuditDrawer;
